import { useEffect, useState } from 'react';

/**
 * Тёмная тема (фаза 1.6). Режим хранится в localStorage `fk-theme`:
 * 'light' | 'dark' | 'system' (по умолчанию — system, по
 * prefers-color-scheme). Применяется атрибутом `data-theme` на <html>
 * (CSS-токены --fk-* в global.css) и antd darkAlgorithm в main.tsx.
 */
export type ThemeMode = 'light' | 'dark' | 'system';

const KEY = 'fk-theme';

/** Порядок и подписи режимов — для переключателя в меню пользователя (HUB 2.0). */
export const THEME_ORDER: ThemeMode[] = ['light', 'dark', 'system'];
export const THEME_LABEL: Record<ThemeMode, string> = { light: 'Светлая', dark: 'Тёмная', system: 'Как в системе' };
const EVENT = 'fk-theme-change';

export function getThemeMode(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function setThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    /* приватный режим — тема только на текущую сессию */
  }
  applyTheme();
  window.dispatchEvent(new Event(EVENT));
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

export function isDarkResolved(mode: ThemeMode = getThemeMode()): boolean {
  return mode === 'dark' || (mode === 'system' && systemDark());
}

/** Проставить data-theme на <html> (вызывать при старте и смене). */
export function applyTheme(): void {
  const dark = isDarkResolved();
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

/** Хук: текущий режим + вычисленная «тёмность»; следит за системной темой. */
export function useThemeMode(): { mode: ThemeMode; dark: boolean; setMode: (m: ThemeMode) => void } {
  const [mode, setModeState] = useState<ThemeMode>(() => getThemeMode());
  const [dark, setDark] = useState<boolean>(() => isDarkResolved());
  useEffect(() => {
    applyTheme();
    const onChange = (): void => {
      setModeState(getThemeMode());
      setDark(isDarkResolved());
    };
    window.addEventListener(EVENT, onChange);
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onMq = (): void => {
      if (getThemeMode() === 'system') {
        applyTheme();
        setDark(isDarkResolved());
      }
    };
    mq?.addEventListener?.('change', onMq);
    return () => {
      window.removeEventListener(EVENT, onChange);
      mq?.removeEventListener?.('change', onMq);
    };
  }, []);
  return { mode, dark, setMode: setThemeMode };
}
